import { useCallback, useState } from 'react';
import {
  NavigationType,
  unstable_useBlocker as useBlocker,
  type unstable_Blocker as Blocker,
  type unstable_BlockerFunction as BlockerFunction,
} from 'react-router-dom';

// Routes match with or without a trailing slash, but `pathname` keeps it.
const stripTrailingSlash = (path: string) => path.replace(/\/+$/, '') || '/';

/**
 * Pathname-only, so SearchScreen's `setSearchParams` filter and query changes never prompt.
 */
export const shouldBlockCartLeave = (
  recipientCount: number,
  { currentLocation, nextLocation }: Parameters<BlockerFunction>[0]
): boolean =>
  recipientCount > 0 &&
  stripTrailingSlash(nextLocation.pathname) !== stripTrailingSlash(currentLocation.pathname);

/**
 * Blocks in-app navigation off a cart page while the cart has recipients. Full page loads
 * (the Donate click, tab close, external links) aren't router navigations and never block.
 *
 * The router supports only one blocker at a time, so this must stay the only one in the app.
 */
export function useLeaveCartGuard(recipientCount: number): {
  blocker: Blocker;
  /** The blocked navigation's action; `Blocker` itself doesn't expose it. */
  historyAction: NavigationType | undefined;
} {
  // State, not a ref: it re-renders in the same batch as the blocker's own state change.
  const [historyAction, setHistoryAction] = useState<NavigationType>();

  const shouldBlock = useCallback<BlockerFunction>(
    args => {
      const block = shouldBlockCartLeave(recipientCount, args);
      if (block) setHistoryAction(args.historyAction);
      return block;
    },
    [recipientCount]
  );

  const blocker = useBlocker(shouldBlock);

  return { blocker, historyAction };
}
